// Generated by GPT-4o
import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Chip,
    TextField,
    Typography,
} from "@mui/material";

import { styled } from '@mui/material/styles';

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        'input': {
            fontSize: '12px'
        },
        '& fieldset': {
            borderColor: 'rgba(139, 142, 146, 1)',
            borderWidth: 1,
        },
        '&:hover fieldset': {
            borderColor: 'rgba(178, 235, 177, 1)',
            borderWidth: 1,
        },
        '&.Mui-focused fieldset': {
            borderColor: 'rgba(178, 235, 177, 1)',
            borderWidth: 1,
        },
        '&.Mui-disabled fieldset': {
            borderColor: 'rgba(175, 175, 175, 1)',
            borderWidth: 1,
        },
        '&.Mui-disabled input': {
            color: 'rgba(175, 175, 175, 1)',
            WebkitTextFillColor: 'rgba(175, 175, 175, 1)', // For Chrome/Safari
        },
    },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
    '&.MuiChip-outlined': {
        borderColor: "rgba(92, 92, 92, 1)",
    },
    '&.MuiChip-outlined:hover': {
        borderColor: 'rgba(178, 235, 177, 1)',
    },
    '&.Mui-disabled': {
        opacity: 1,
    },
    "& .MuiChip-label": {
        color: "rgb(255, 255, 255)",
    },
    '&.Mui-disabled .MuiChip-label': {
        color: 'rgba(175, 175, 175, 1)', // For Chrome/Safari
    },
    "& .MuiChip-deleteIcon": {
        color: 'rgba(92, 92, 92, 1)',
    },
    "& .MuiChip-deleteIcon:hover": {
            color: 'rgba(222, 155, 145, 1)'
    },
    "&.Mui-disabled .MuiChip-deleteIcon ": {
        display: "none",
    },
}));

export default function SettingsChipTextField({
    description='',
    disabled=false,
    label='',
    onChange= () => {},
    original=[],
    values = [],
}) {
    const [commitValues, setCommitValues] = useState(values);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [editValues, setEditValues] = useState(values);
    const [inputValue, setInputValue] = useState('');
    const [focused, setFocused] = useState(false);
    const [targetIndex, setTargetIndex] = useState(null);
    const [uncommittedChanges, setUncommittedChanges] = useState(false);

    useEffect(() => {
        setCommitValues(values);
        setEditValues(values);
    }, [values]);

    useEffect(() => {
        JSON.stringify(original) === JSON.stringify(commitValues) ? setUncommittedChanges(false) : setUncommittedChanges(true);
    }, [commitValues, original]);

    const handleChange = (values) => {
        setEditValues(values);
        onChange(values);
    }

    // Handles removing a chip
    const handleDelete = (chipToDelete) => {
        handleChange(editValues.filter((chip) => chip !== chipToDelete));
    };

    // Handles drag over (prevents default behavior to allow dropping)
    const handleDragOver = (e, index) => {
        e.preventDefault();
        setTargetIndex(index);
    };

    // Handles drag start
    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    // Handles drop event
    const handleDrop = (index) => {
        if (draggedIndex === null) return;

        let updatedChips = [...editValues];
        const draggedChip = updatedChips.splice(draggedIndex, 1)[0]; // Remove dragged item
        updatedChips.splice(index, 0, draggedChip); // Insert at target index

        handleChange(updatedChips);
        setDraggedIndex(null);
        setTargetIndex(null);
    };

    // Handles adding a chip on Enter or Comma
    const handleKeyDown = (e) => {
        if (e.key === 'Backspace' && inputValue === "") {
            // Remove the last chip if Backspace is pressed and the input is empty
            handleChange(editValues.slice(0, -1));
        }
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const trimmedValue = inputValue.trim();

            if (trimmedValue && !editValues.includes(trimmedValue)) {
                handleChange([...editValues, trimmedValue]);
            }
            setInputValue("");
        }
    };

    const handlePaste = (event) => {
        // Prevent the default paste behavior
        event.preventDefault();
        // Get the pasted text and split it by commas
        const pastedText = event.clipboardData.getData('text');
        const newChips = pastedText
          .split(',')
          .map(chip => chip.trim())  // Trim any leading/trailing spaces
          .filter(chip => chip !== '' && !editValues.includes(chip));  // Remove empty strings
        // Add the new editValues to the existing list
        handleChange([...editValues, ...newChips]);
      };

    return (
        <Box
            display="flex"
            flexDirection="column"
            m={1}
            position='relative'
        >
            {
                uncommittedChanges && 
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    mx={-1}
                    my={0.7}
                >
                    <Box
                        width={5}
                        height={5}
                        borderRadius={5}
                        backgroundColor={'rgba(255, 193, 7, 1)'} // warning color (amber)
                    />
                </Box>
            }
            <Typography
                variant="subtitle2"
                fontWeight={900}
                fontSize="12px"
                sx={{
                    color: disabled ? 'rgba(175, 175, 175, 1)'
                        : focused ? 'rgba(178, 235, 177, 1)'
                        : 'rgba(255, 255, 255, 1)'
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="subtitle2"
                fontWeight={600}
                fontSize="11px"
                color="rgb(150, 150, 150)"
            >
                {description}
            </Typography>
            <Box
                display='flex'
                gap={1}
                py={1}
                flexWrap='wrap'
            >
                {editValues.map((value, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        {/* Drop Indicator - Shows when dragging over */}
                        {targetIndex === index && (
                            <Box
                                sx={{
                                    width: "2px",
                                    height: "30px",
                                    backgroundColor: "rgba(255, 255, 255, 1)",
                                    borderRadius: "2px",
                                    marginRight: "4px",
                                }}
                            />
                        )}
                        <StyledChip
                            label={value}
                            draggable
                            variant="outlined"
                            disabled={disabled}
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(event) => handleDragOver(event, index)}
                            onDrop={() => handleDrop(index)}
                            onDelete={() => handleDelete(value)}
                            // deleteIcon={disabled ? <span /> : undefined} // Hide icon if disabled
                            sx={{
                                // cursor: disabled ? "default" : "grab",
                                // '&:hover': {
                                //     border: disabled ? undefined : "1px solid rgba(178, 235, 177, 1)",
                                // },
                            }}
                        />
                    </Box>
                ))}
            </Box>
            {!disabled && (
                <Box width="250px">
                    <StyledTextField
                        fullWidth
                        disabled={disabled}
                        variant="outlined"
                        size="small"
                        sx={{ input: { color: 'white' } }}
                        value={inputValue}
                        onChange={(event) => setInputValue(event.target.value)}
                        onFocus={() => {
                            setFocused(true);
                        }}
                        onBlur={() => {
                            setFocused(false);
                        }}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        placeholder="Type and press comma or enter..."
                    />
                </Box>
            )}
        </Box>
    );
};
